import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createSlug } from '../../util/urlHelpers';
import { DELIVERY_METHOD_PICKUP, DELIVERY_METHOD_SHIPPING } from '../../util/cart';

import { InlineTextButton, NamedLink } from '../../components';

import {
  ROW_STATE_SOLD,
  SHIPPING_ROLE_BOX_RATE,
  SHIPPING_ROLE_RIDER,
  SHIPPING_ROLE_SOLO,
  canAdjustQuantity,
  canSwitchDeliveryMethod,
  hasDeliveryMethodFallback,
  hasPriceDiff,
  isDeadRow,
  lineItemTotalSubunits,
  riderSurchargeForRow,
  shippingRoleForRow,
} from './CartPage.shared';
import css from './CartRow.module.css';

const { Money } = sdkTypes;

const MAX_QUANTITY = 100;

const money = (intl, amount, currency) =>
  amount != null && currency ? formatMoney(intl, new Money(amount, currency)) : null;

const RowTitle = ({ row, isDead }) => {
  const title = row.title || null;
  if (!title) {
    return (
      <span className={css.titleMissing}>
        <FormattedMessage id="CartRow.untitledListing" />
      </span>
    );
  }
  return isDead ? (
    <span className={css.title}>{title}</span>
  ) : (
    <NamedLink
      className={css.titleLink}
      name="ListingPage"
      params={{ id: row.listingId, slug: createSlug(title) }}
    >
      {title}
    </NamedLink>
  );
};

const PriceLine = ({ row, intl }) => {
  const qty = row.qty || 1;
  const liveTotal = money(intl, lineItemTotalSubunits(row), row.currency);
  const wasTotal =
    typeof row.unitPriceAtAdd === 'number'
      ? money(intl, row.unitPriceAtAdd * qty, row.currency)
      : null;
  const liveUnit = money(intl, row.unitPrice, row.currency);

  if (!liveTotal) {
    return null;
  }

  return (
    <div className={css.priceBlock}>
      {hasPriceDiff(row) && wasTotal ? (
        <p className={css.priceLine}>
          <span className={css.priceWas}>{wasTotal}</span>
          <span className={css.priceNow}>{liveTotal}</span>
          <span className={css.priceChangedTag}>
            <FormattedMessage id="CartRow.priceChangedTag" />
          </span>
        </p>
      ) : (
        <p className={css.priceLine}>
          <span className={css.priceNow}>{liveTotal}</span>
        </p>
      )}
      {qty > 1 && liveUnit ? (
        <p className={css.unitEach}>
          <FormattedMessage id="CartRow.unitPriceEach" values={{ qty, amount: liveUnit }} />
        </p>
      ) : null}
    </div>
  );
};

const ShippingRole = ({ row, ledger, intl }) => {
  const role = shippingRoleForRow(row, ledger);
  if (!role) {
    return null;
  }
  if (role === SHIPPING_ROLE_BOX_RATE) {
    return (
      <p className={css.roleLine}>
        <FormattedMessage
          id="CartRow.setsBoxRate"
          values={{ amount: money(intl, ledger?.firstItemFee, row.currency) }}
        />
      </p>
    );
  }
  if (role === SHIPPING_ROLE_RIDER) {
    const surcharge = riderSurchargeForRow(row, ledger);
    return (
      <p className={css.roleLine}>
        {surcharge > 0 ? (
          <FormattedMessage
            id="CartRow.ridesAlongWithFee"
            values={{ amount: money(intl, surcharge, row.currency) }}
          />
        ) : (
          <FormattedMessage id="CartRow.ridesAlong" />
        )}
      </p>
    );
  }
  if (role === SHIPPING_ROLE_SOLO) {
    return (
      <p className={css.roleLine}>
        <FormattedMessage
          id="CartRow.soloMeta"
          values={{ packingClass: row.packingClass || 'other', deliveryMethod: row.deliveryMethod }}
        />
      </p>
    );
  }
  return null;
};

const DeliveryMethodToggle = ({ row, onUpdateDeliveryMethod, disabled, intl }) => {
  const isPickup = row.deliveryMethod === DELIVERY_METHOD_PICKUP;
  const select = method => () => {
    if (method !== row.deliveryMethod) {
      onUpdateDeliveryMethod({ listingId: row.listingId, deliveryMethod: method });
    }
  };

  return (
    <div
      className={css.methodToggle}
      role="group"
      aria-label={intl.formatMessage({ id: 'CartRow.deliveryMethodLabel' })}
    >
      <button
        type="button"
        className={classNames(css.methodOption, { [css.methodOptionSelected]: !isPickup })}
        onClick={select(DELIVERY_METHOD_SHIPPING)}
        aria-pressed={!isPickup}
        disabled={disabled}
      >
        <FormattedMessage id="CartRow.shippingOption" />
      </button>
      <button
        type="button"
        className={classNames(css.methodOption, { [css.methodOptionSelected]: isPickup })}
        onClick={select(DELIVERY_METHOD_PICKUP)}
        aria-pressed={isPickup}
        disabled={disabled}
      >
        <FormattedMessage id="CartRow.pickupOption" />
      </button>
    </div>
  );
};

const QuantityStepper = ({ row, maxQty, disabled, onStep, intl }) => {
  const title = row.title || intl.formatMessage({ id: 'CartRow.untitledListing' });
  return (
    <div className={css.stepper}>
      <button
        type="button"
        className={css.stepperBtn}
        disabled={disabled || row.qty <= 1}
        aria-label={intl.formatMessage({ id: 'CartRow.decreaseQuantity' }, { title })}
        onClick={() => onStep(-1)}
      >
        −
      </button>
      <span className={css.stepperValue} aria-live="polite">
        {row.qty}
      </span>
      <button
        type="button"
        className={css.stepperBtn}
        disabled={disabled || row.qty >= maxQty}
        aria-label={intl.formatMessage({ id: 'CartRow.increaseQuantity' }, { title })}
        onClick={() => onStep(1)}
      >
        +
      </button>
    </div>
  );
};

/**
 * One cart row: one listing, in one box.
 *
 * Title and subline sit beside the price; Remove sits under the price.
 * Qty/delivery sit on a second row. Dead rows stay visible and expose only Remove.
 *
 * @component
 */
const CartRow = props => {
  const {
    row,
    ledger = null,
    isUpdating = false,
    disabled = false,
    conflictedOnMerge = false,
    onRemove,
    onUpdateQty,
    onUpdateDeliveryMethod,
  } = props;
  const intl = useIntl();
  const [clampAcknowledged, setClampAcknowledged] = useState(false);

  const isDead = isDeadRow(row);
  const isSold = row.state === ROW_STATE_SOLD;
  const writesBlocked = isUpdating || disabled;
  const showStepper = canAdjustQuantity(row);
  const showDeliveryToggle = canSwitchDeliveryMethod(row);
  const showControls = showStepper || showDeliveryToggle;
  const maxQty = showStepper ? Math.min(row.stock, MAX_QUANTITY) : 1;

  const handleQtyStep = delta => {
    const nextQty = row.qty + delta;
    setClampAcknowledged(true);
    if (!Number.isInteger(nextQty) || nextQty < 1 || nextQty === row.qty) {
      return;
    }
    if (showStepper && nextQty > maxQty) {
      return;
    }
    onUpdateQty({ listingId: row.listingId, qty: nextQty });
  };

  return (
    <li
      className={classNames(css.root, {
        [css.rootDead]: isDead,
        [css.rootWithControls]: showControls,
      })}
    >
      <div className={css.media} aria-hidden="true">
        {row.imageUrl ? (
          <img className={css.image} src={row.imageUrl} alt="" />
        ) : (
          <div className={css.imagePlaceholder} />
        )}
        {isDead ? <span className={css.hatch} /> : null}
      </div>

      <div className={css.details}>
        <RowTitle row={row} isDead={isDead} />
        <ShippingRole row={row} ledger={ledger} intl={intl} />

        {isDead ? (
          <p className={css.deadNotice} role="status">
            <FormattedMessage id={isSold ? 'CartRow.soldNotice' : 'CartRow.unlistedNotice'} />
          </p>
        ) : null}

        {!isDead && row.qtyClamped && !clampAcknowledged ? (
          <p className={css.warnFlag} role="status">
            <FormattedMessage
              id="CartRow.stockReducedFlag"
              values={{ stock: row.stock, qty: row.qty }}
            />
          </p>
        ) : null}

        {hasDeliveryMethodFallback(row) ? (
          <p className={css.warnFlag} role="status">
            <FormattedMessage
              id={
                row.deliveryMethod === DELIVERY_METHOD_PICKUP
                  ? 'CartRow.methodFallbackToPickup'
                  : 'CartRow.methodFallbackToShipping'
              }
            />
          </p>
        ) : null}

        {conflictedOnMerge && !isDead ? (
          <p className={css.warnFlag} role="status">
            <FormattedMessage id="CartRow.mergeConflictFlag" values={{ qty: row.qty }} />
          </p>
        ) : null}
      </div>

      <div className={css.actions}>
        <PriceLine row={row} intl={intl} />
        <InlineTextButton
          className={css.removeButton}
          type="button"
          onClick={() => onRemove(row.listingId)}
          disabled={writesBlocked}
        >
          <FormattedMessage id="CartRow.remove" />
        </InlineTextButton>
      </div>

      {showControls ? (
        <div className={css.controls}>
          {isDead || !showStepper ? null : (
            <QuantityStepper
              row={row}
              maxQty={maxQty}
              disabled={writesBlocked}
              onStep={handleQtyStep}
              intl={intl}
            />
          )}
          {showDeliveryToggle ? (
            <DeliveryMethodToggle
              row={row}
              onUpdateDeliveryMethod={onUpdateDeliveryMethod}
              disabled={writesBlocked}
              intl={intl}
            />
          ) : null}
        </div>
      ) : null}
    </li>
  );
};

export default CartRow;
